import { useEffect, useState } from 'react';
import { fetchAiChatStats, EMPTY_AI_CHAT_STATS, type AiChatStats } from '@/lib/aiChatStats';

interface Props {
  /** Marchand concerné, ou plusieurs marchands (agrégation marketplace). */
  merchantIds: number[];
  /** Titre affiché (par défaut « Assistant IA »). */
  title?: string;
  /** Affiche les dernières interactions. */
  showRecent?: boolean;
  /** Fenêtre en jours (0 = tout l'historique). */
  days?: number;
}

const EVENT_META: Record<string, { label: string; icon: string; className: string }> = {
  display: { label: 'Affichage', icon: 'ri-eye-line', className: 'text-secondary-700 bg-secondary-100' },
  click: { label: 'Ouverture', icon: 'ri-cursor-line', className: 'text-accent-700 bg-accent-100' },
  cart: { label: 'Panier', icon: 'ri-shopping-cart-2-line', className: 'text-primary-700 bg-primary-100' },
  boutique: { label: 'Boutique vue', icon: 'ri-store-2-line', className: 'text-primary-700 bg-primary-100' },
};

function eventMeta(type: string) {
  return EVENT_META[type] || { label: type || '—', icon: 'ri-robot-2-line', className: 'text-foreground-600 bg-background-100' };
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} j`;
}

/**
 * Carte de statistiques de l'assistant IA : affichages, ouvertures de fiche,
 * ajouts au panier et meilleurs contenus, issus des interactions réelles des
 * clients dans le chat IA.
 */
export default function AiChatStatsCard({ merchantIds, title = 'Assistant IA', showRecent = true, days = 0 }: Props) {
  const [stats, setStats] = useState<AiChatStats>(EMPTY_AI_CHAT_STATS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchAiChatStats(merchantIds, { days })
      .then((s) => { if (active) setStats(s); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [JSON.stringify(merchantIds), days]);

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 md:px-5 py-4 border-b border-background-200/70">
        <span className="w-9 h-9 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-robot-2-line text-lg"></i>
        </span>
        <div>
          <h3 className="text-sm font-semibold text-foreground-900">{title}</h3>
          <p className="text-[11px] text-foreground-500">Activité générée par l&apos;assistant IA {days > 0 ? `(sur ${days} jours)` : ''}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
        </div>
      ) : stats.total === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
          <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-2">
            <i className="ri-robot-2-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm text-foreground-500">Aucune interaction pour le moment.</p>
          <p className="text-[11px] text-foreground-400 mt-1">Les recherches des clients dans l&apos;assistant IA apparaîtront ici.</p>
        </div>
      ) : (
        <div className="p-4 md:p-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="rounded-lg bg-secondary-50 border border-secondary-100 p-3">
              <div className="text-xl font-bold text-foreground-950 tabular-nums">{stats.display.toLocaleString()}</div>
              <div className="text-[11px] text-foreground-500 mt-0.5">Affichages</div>
            </div>
            <div className="rounded-lg bg-accent-50 border border-accent-100 p-3">
              <div className="text-xl font-bold text-foreground-950 tabular-nums">{stats.click.toLocaleString()}</div>
              <div className="text-[11px] text-foreground-500 mt-0.5">Ouvertures</div>
            </div>
            <div className="rounded-lg bg-primary-50 border border-primary-100 p-3">
              <div className="text-xl font-bold text-foreground-950 tabular-nums">{stats.cart.toLocaleString()}</div>
              <div className="text-[11px] text-foreground-500 mt-0.5">Ajouts au panier</div>
            </div>
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-3">
              <div className="text-xl font-bold text-foreground-950 tabular-nums">{stats.uniqueClients.toLocaleString()}</div>
              <div className="text-[11px] text-foreground-500 mt-0.5">Clients uniques</div>
            </div>
          </div>

          {stats.boutiqueViews > 0 && (
            <div className="mb-4 flex items-center gap-3 px-3.5 py-3 rounded-lg bg-primary-50 border border-primary-100">
              <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-store-2-line"></i>
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground-900">
                  Votre fiche boutique a été ouverte <span className="text-primary-700 tabular-nums">{stats.boutiqueViews.toLocaleString()}</span> fois via l&apos;IA
                </div>
                <div className="text-[11px] text-foreground-500">Clients ayant consulté votre boutique depuis l&apos;assistant IA.</div>
              </div>
            </div>
          )}

          {stats.topItems.length > 0 && (
            <div className="mb-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400 mb-2">Contenus les plus consultés</p>
              <div className="space-y-1.5">
                {stats.topItems.map((it) => (
                  <div key={it.name} className="flex items-center gap-3 text-sm">
                    <i className="ri-price-tag-3-line text-accent-500 flex-shrink-0"></i>
                    <span className="truncate flex-1 text-foreground-700">{it.name}</span>
                    <span className="text-[11px] text-foreground-500 whitespace-nowrap">{it.total} interaction{it.total > 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showRecent && stats.recent.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400 mb-2">Dernières interactions</p>
              <div className="space-y-1">
                {stats.recent.map((r) => {
                  const meta = eventMeta(r.eventType);
                  return (
                    <div key={r.id} className="flex items-center gap-2.5 py-1.5 border-b border-background-200/50 last:border-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${meta.className}`}>
                        <i className={`${meta.icon} text-[10px]`}></i>{meta.label}
                      </span>
                      <span className="text-xs text-foreground-700 truncate flex-1">{r.productName || r.query || 'Recherche'}</span>
                      {r.clientName && <span className="text-[11px] text-foreground-400 truncate max-w-[90px] hidden sm:block">{r.clientName}</span>}
                      <span className="text-[11px] text-foreground-400 whitespace-nowrap">{timeAgo(r.createdAt)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}